import { keys, keyBy, isNumber, isString } from 'lodash'

import { getText } from '../lib/text.js'

type Status = { value: number, name: string, label: string }

const statuses = [
  {
    value: 0,
    name: "unk",
    icon: "❓ ",
    label: () => ("❓  " + getText("Otros")),
    shortLabel: () => ("❓  " + getText("sh_Otros")),
    esLabel: "❓  " + getText("Otros", "es")
  },
  {
    value: 10,
    name: "new",
    icon: "➕ ",
    label: () => ("➕  " + getText("Nuevo cliente")),
    shortLabel: () => ("➕  " + getText("sh_Nuevo cliente")),
    esLabel: "➕  " + getText("Nuevo cliente", "es")
  },
  {
    value: 20,
    name: "proposal_sent",
    icon: "📬 ",
    label: () => ("📬  " + getText("1a Propuesta")),
    shortLabel: () => ("📬  " + getText("sh_1a Propuesta")),
    esLabel: "📬  " + getText("1a Propuesta", "es")
  },
  {
    value: 25,
    name: "negotiating",
    icon: "📬 ",
    label: () => ("📬  " + getText("Negociando")),
    shortLabel: () => ("📬  " + getText("sh_Negociando")),
    esLabel: "📬  " + getText("Negociando", "es")
  },
  {
    value: 30,
    name: "contract_sent",
    icon: "📃 ",
    label: () => ("📃  " + getText("Contrato enviado")),
    shortLabel: () => ("📃  " + getText("sh_Contrato enviado")),
    esLabel: "📃  " + getText("Contrato enviado", "es")
  },
  {
    value: 39,
    name: "accepted_no_bond",
    icon: "👍 ",
    label: () => ("👍  " + getText("aprobado_sin_fianza")),
    shortLabel: () => ("👍  " + getText("sh_aprobado_sin_fianza")),
    esLabel: "👍  " + getText("aprobado_sin_fianza", "es")
  },
  {
    value: 40,
    name: "bond_sent",
    icon: "💸 ",
    label: () => ("💸  " + getText("Fianza pagada")),
    shortLabel: () => ("💸  " + getText("sh_Fianza pagada")),
    esLabel: "💸  " + getText("Fianza pagada", "es")
  },
  {
    value: 50,
    name: "first_payment_sent",
    icon: "💸 ",
    label: () => ("💸  " + getText("Pago parcial")),
    shortLabel: () => ("💸  " + getText("sh_Pago parcial")),
    esLabel: "💸  " + getText("Pago parcial", "es")
  },
  {
    value: 60,
    name: "all_paid",
    icon: "💸 ",
    label: () => ("💸  " + getText("Pago completo")),
    shortLabel: () => ("💸  " + getText("sh_Pago completo")),
    esLabel: "💸  " + getText("Pago completo", "es")
  },
  {
    value: 70,
    name: "done",
    icon: "☑️  ",
    label: () => ("☑️  " + getText("Cerrado")),
    shortLabel: () => ("☑️  " + getText("sh_Cerrado")),
    esLabel: "☑️  " + getText("Cerrado", "es")
  },
  {
    value: 100,
    name: "lost",
    icon: "😔 ",
    label: () => ("😔  " + getText("Perdido")),
    shortLabel: () => ("😔  " + getText("sh_Perdido")),
    esLabel: "😔  " + getText("Perdido", "es")
  },
]

const statusByValue = keyBy(statuses, "value")
const statusByName = keyBy(statuses, "name")
const statusByLabel = keyBy(statuses, "label")

const statusValues = statuses.map(x => x.value).sort((a, b) => a - b)
const statusNames = statuses.map(x => x.name)

/**
 * returns true if there is a status by that value or name
 */
function isValidStatus(k: number|string): boolean { 
  return !!(statusByValue[k] || statusByName[k]) 
}

/**
 * retrieves an status by name or by value
 */
function getStatus(k: number|string): Status {  
  if (isNumber(k) && statusByValue[k]) return statusByValue[k]
  if (isString(k) && statusByName[k]) return statusByName[k]
  return statusByValue[0]
}

/**
 *
 */
export function getStatusName(k: number|string): string { 
  return getStatus(k).name 
}

/**
 *
 */
export function getStatusIcon(k: number|string): string { 
  return getStatus(k).icon 
}

/**
 * 
 */
export function getStatusLabel(k: number|string, opt = { forceSpanish: false, shorter: false }): string { 
  if (opt.forceSpanish) return getStatus(k).esLabel
  else if (opt.shorter) return getStatus(k).shortLabel()
  else return getStatus(k).label()
}

/** 
 *
 */
export function getStatusValue(k: number|string): number { 
  return getStatus(k).value 
}

export const UNKNOWN = getStatusValue("unk")
export const OTHER = getStatusValue("unk")
export const NEW = getStatusValue("new")
export const PROPOSAL_SENT = getStatusValue("proposal_sent")
export const NEGOTIATING = getStatusValue("negotiating")
export const CONTRACT_SENT = getStatusValue("contract_sent")
export const ACCEPTED_NO_BOND = getStatusValue("accepted_no_bond")
export const BOND_SENT = getStatusValue("bond_sent")
export const FIRST_PAYMENT_SENT = getStatusValue("first_payment_sent")
export const ALL_PAID = getStatusValue("all_paid")
export const DONE = getStatusValue("done")
export const LOST = getStatusValue("lost")


/**
 * Get an array with all status names
 */
export function getStatusNames(): string[] { 
  return statusNames 
}

/**
 * Get an array with all status values
 */
export function getStatusValues(): number[] { 
  return statusValues
}

/**
 * Pestañas en OperPage, estados incluidos
 */
const _tabLimits = {
  "otros":      { minState: 0,   maxState: 0  }, 
  "nuevos":     { minState: 10,  maxState: 10 }, 
  "propuesta":  { minState: 20,  maxState: 20 }, 
  "negociando": { minState: 25,  maxState: 25 }, 
  "contrato":   { minState: 30,  maxState: 30 }, 
  "aprobados":  { minState: 39,  maxState: 60 }, 
  "cerrados":   { minState: 70,  maxState: 70 }, 
  "perdidos":   { minState: 100, maxState: 100 }, 
}

export function getTabStatusLimits(tab: string): { min: number, max: number } {
  return _tabLimits[tab] || getTabStatusLimits("nuevos")
}

/**
 * 
 */
export function getTripTab(trip: object): number {
  const { status } = trip
  if (status == 0)  return "otros"
  if (status == 10) return "nuevos"
  if (status == 20) return "propuesta"
  if (status == 25) return "negociando"
  if (status == 30) return "contrato"
  if ( 39 <= status && status <=  60) return "aprobados"
  if (status ==  70) return "cerrados"
  if (status == 100) return "perdidos"
  else return -1
}

/**
 * 
 */
export function getTabLabel(tab: number): string {
  if (tab === "otros")      return "❓  " + getText("Otros")
  if (tab === "spot")       return "❓  " + getText("Spot")
  if (tab === "agencia")    return "❓  " + getText("tabAgencia")
  if (tab === "nuevos")     return "➕  " + getText("Nuevos")
  if (tab === "propuesta")  return "📬  " + getText("1a Propuesta")
  if (tab === "negociando") return "📬  " + getText("Negociando")
  if (tab === "contrato")   return "📃  " + getText("Contrato")
  if (tab === "aprobados")  return "👍  " + getText("Aprobados")
  if (tab === "cerrados")   return "💵  " + getText("Cerrados")
  if (tab === "perdidos")   return "😔  " + getText("Perdidos")
  
  console.error(`WTF is tab ${tab}`)
  return `WTF is tab ${tab}`
}
